import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const VARIABLE_MENU_GAP = 4;
const VARIABLE_MENU_WIDTH = 320;

const FormulaBar = ({
  columnKey,
  inputName,
  onChange,
  onFinish,
  onKeyDown,
  onKeyUp,
  value,
  variables,
}) => {
  const formulaBarRef = useRef(null);
  const inputRef = useRef(null);
  const variableButtonRef = useRef(null);
  const [isVariableMenuOpen, setIsVariableMenuOpen] = useState(false);
  const [variableMenuPosition, setVariableMenuPosition] = useState(null);

  const updateVariableMenuPosition = useCallback(() => {
    const rect = variableButtonRef.current?.getBoundingClientRect?.();
    if (!rect || typeof window === 'undefined') return;

    setVariableMenuPosition({
      left: Math.max(
        8,
        Math.min(
          rect.right - VARIABLE_MENU_WIDTH,
          window.innerWidth - 8 - VARIABLE_MENU_WIDTH
        )
      ),
      top: rect.bottom + VARIABLE_MENU_GAP,
      width: VARIABLE_MENU_WIDTH,
    });
  }, []);

  useEffect(() => {
    if (!isVariableMenuOpen) return undefined;

    updateVariableMenuPosition();
    window.addEventListener('resize', updateVariableMenuPosition);
    window.addEventListener('scroll', updateVariableMenuPosition, true);

    return () => {
      window.removeEventListener('resize', updateVariableMenuPosition);
      window.removeEventListener('scroll', updateVariableMenuPosition, true);
    };
  }, [isVariableMenuOpen, updateVariableMenuPosition]);

  const emitChange = (nextValue, cursorPosition, variable) => {
    onChange({
      target: {
        dataset: { formulaBarInput: 'true' },
        globalSuggestion: variable,
        name: inputName,
        selectionEnd: cursorPosition,
        selectionStart: cursorPosition,
        value: nextValue,
      },
    });
  };

  const insertVariable = variable => {
    const currentValue = String(value ?? '');
    const selectionStart =
      inputRef.current?.selectionStart ?? currentValue.length;
    const selectionEnd = inputRef.current?.selectionEnd ?? selectionStart;
    const nextValue = `${currentValue.slice(0, selectionStart)}${
      variable.abbreviation
    }${currentValue.slice(selectionEnd)}`;
    const nextCursor = selectionStart + variable.abbreviation.length;

    emitChange(nextValue, nextCursor, variable);
    setIsVariableMenuOpen(false);

    window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange?.(nextCursor, nextCursor);
    }, 0);
  };

  const handleBlur = event => {
    const nextTarget = event.relatedTarget;
    if (
      formulaBarRef.current?.contains(nextTarget) ||
      nextTarget?.closest?.('.detail-formula-variable-menu')
    ) {
      return;
    }

    setIsVariableMenuOpen(false);
    onFinish();
  };

  const variableMenu =
    isVariableMenuOpen &&
    variableMenuPosition &&
    typeof document !== 'undefined'
      ? createPortal(
          <div
            aria-label="Formula variables"
            className="detail-formula-variable-menu"
            data-formula-bar-column={columnKey}
            data-formula-bar-input-name={inputName}
            onBlur={handleBlur}
            role="listbox"
            style={variableMenuPosition}
          >
            {variables.map(variable => (
              <button
                aria-label={`Insert ${variable.abbreviation}`}
                aria-selected="false"
                className="detail-formula-variable-menu__item"
                key={variable.id || variable.abbreviation}
                onClick={() => insertVariable(variable)}
                role="option"
                type="button"
              >
                <span className="detail-formula-variable-menu__abbreviation">
                  {variable.abbreviation}
                </span>
                <span className="detail-formula-variable-menu__name">
                  {variable.name || variable.abbreviation}
                </span>
                <span className="detail-formula-variable-menu__value">
                  {[variable.displayValue, variable.units]
                    .filter(Boolean)
                    .join(' ')}
                </span>
              </button>
            ))}
          </div>,
          document.body
        )
      : null;

  return (
    <div
      className="detail-formula-bar"
      data-formula-bar-column={columnKey}
      data-formula-bar-input-name={inputName}
      onBlur={handleBlur}
      ref={formulaBarRef}
    >
      <label
        className="detail-formula-bar__label"
        htmlFor="detail-formula-input"
      >
        fx
      </label>
      <input
        aria-label="Formula"
        autoComplete="off"
        className="detail-formula-bar__input"
        data-formula-bar-input="true"
        id="detail-formula-input"
        name={inputName}
        onChange={event => onChange(event)}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        ref={inputRef}
        spellCheck="false"
        type="text"
        value={value ?? ''}
      />
      <button
        aria-expanded={isVariableMenuOpen}
        aria-haspopup="listbox"
        className="detail-formula-bar__variables"
        disabled={!variables.length}
        onClick={() => setIsVariableMenuOpen(isOpen => !isOpen)}
        ref={variableButtonRef}
        title={
          variables.length ? 'Insert a global variable' : 'No globals available'
        }
        type="button"
      >
        Variables
      </button>
      {variableMenu}
    </div>
  );
};

FormulaBar.propTypes = {
  columnKey: PropTypes.string.isRequired,
  inputName: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onFinish: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func,
  onKeyUp: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  variables: PropTypes.arrayOf(
    PropTypes.shape({
      abbreviation: PropTypes.string.isRequired,
      displayValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string,
      units: PropTypes.string,
    })
  ),
};

FormulaBar.defaultProps = {
  onKeyDown: () => null,
  onKeyUp: () => null,
  value: '',
  variables: [],
};

export default FormulaBar;
